import React, { useEffect, useState } from 'react';
import {
  IonButton, IonContent, IonIcon, IonInput, IonModal, IonPage,
  IonSkeletonText, IonSpinner, IonToast, useIonAlert,
} from '@ionic/react';
import {
  addOutline, alertCircleOutline, atOutline, closeOutline, eyeOffOutline, eyeOutline,
  keyOutline, lockClosedOutline, pencilOutline, personOutline, powerOutline, shieldCheckmarkOutline,
} from 'ionicons/icons';
import { API_BASE_URL, authHeaders } from '../../config';
import { PageHeader, AppBackground } from '../../components/PageHeader';
import { useUser } from '../../context/UserContext';
import './usuarios.css';

// ── Tipos ────────────────────────────────────────────────────────────────────

type Rol = 'admin' | 'asistente';

interface Usuario {
  id: number;
  nombre: string;
  nickname: string;
  rol: Rol;
  activo: boolean;
  total_referidos: number;
}

// Qué muestra el diálogo: crear, editar datos o restablecer contraseña
type Dialogo =
  | { modo: 'crear' }
  | { modo: 'editar'; usuario: Usuario }
  | { modo: 'password'; usuario: Usuario };

const PASSWORD_MIN = 6;
const ROLES: { valor: Rol; etiqueta: string }[] = [
  { valor: 'asistente', etiqueta: 'Asistente' },
  { valor: 'admin', etiqueta: 'Administrador' },
];

// ── Componente ───────────────────────────────────────────────────────────────

const Usuarios: React.FC = () => {
  const { user } = useUser();
  const [presentAlert] = useIonAlert();

  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorLista, setErrorLista] = useState('');
  const [toast, setToast] = useState('');

  const [dialogo, setDialogo] = useState<Dialogo | null>(null);
  const [nombre, setNombre] = useState('');
  const [nickname, setNickname] = useState('');
  const [rol, setRol] = useState<Rol>('asistente');
  const [password, setPassword] = useState('');
  const [verPassword, setVerPassword] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [errorForm, setErrorForm] = useState('');

  // ── API ──────────────────────────────────────────────────────────────────

  const fetchUsuarios = async () => {
    setLoading(true);
    setErrorLista('');
    try {
      const res = await fetch(`${API_BASE_URL}/usuarios`, { headers: authHeaders() });
      const result = await res.json();
      if (!res.ok || result.status !== 'success') {
        setErrorLista(result.message || result.error || 'No se pudieron cargar los usuarios.');
        return;
      }
      setUsuarios(result.data);
    } catch {
      setErrorLista('No se pudo conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsuarios(); }, []);

  /** Envía un cambio y devuelve el mensaje de error, o '' si salió bien. */
  const enviar = async (ruta: string, method: 'POST' | 'PUT', body: object): Promise<string> => {
    try {
      const res = await fetch(`${API_BASE_URL}${ruta}`, {
        method,
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(body),
      });
      const result = await res.json();
      return res.ok && result.status === 'success' ? '' : (result.message || result.error || 'No se pudo guardar.');
    } catch {
      return 'No se pudo conectar con el servidor.';
    }
  };

  // ── Diálogo ──────────────────────────────────────────────────────────────

  const abrir = (d: Dialogo) => {
    const u = d.modo === 'crear' ? null : d.usuario;
    setNombre(u?.nombre ?? '');
    setNickname(u?.nickname ?? '');
    setRol(u?.rol ?? 'asistente');
    setPassword('');
    setVerPassword(false);
    setErrorForm('');
    setDialogo(d);
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dialogo) return;

    const pidePassword = dialogo.modo !== 'editar';
    if (dialogo.modo !== 'password' && (!nombre.trim() || !nickname.trim())) {
      setErrorForm('El nombre y el usuario son obligatorios.');
      return;
    }
    if (pidePassword && password.length < PASSWORD_MIN) {
      setErrorForm(`La contraseña debe tener al menos ${PASSWORD_MIN} caracteres.`);
      return;
    }

    setGuardando(true);
    setErrorForm('');
    let error: string;
    let exito: string;
    if (dialogo.modo === 'crear') {
      error = await enviar('/usuarios', 'POST', { nombre: nombre.trim(), nickname: nickname.trim(), rol, password });
      exito = 'Usuario creado';
    } else if (dialogo.modo === 'editar') {
      error = await enviar(`/usuarios/${dialogo.usuario.id}`, 'PUT', { nombre: nombre.trim(), nickname: nickname.trim(), rol });
      exito = 'Usuario actualizado';
    } else {
      error = await enviar(`/usuarios/${dialogo.usuario.id}/password`, 'PUT', { password });
      exito = `Contraseña de ${dialogo.usuario.nombre} restablecida`;
    }
    setGuardando(false);

    if (error) {
      setErrorForm(error);
      return;
    }
    setDialogo(null);
    setToast(exito);
    fetchUsuarios();
  };

  // ── Activar / desactivar ─────────────────────────────────────────────────

  const cambiarEstado = async (u: Usuario) => {
    const error = await enviar(`/usuarios/${u.id}`, 'PUT', { activo: !u.activo });
    setToast(error || (u.activo ? `${u.nombre} fue desactivado` : `${u.nombre} fue activado`));
    if (!error) fetchUsuarios();
  };

  const confirmarEstado = (u: Usuario) => {
    if (!u.activo) {
      cambiarEstado(u);
      return;
    }
    presentAlert({
      header: 'Desactivar usuario',
      message: `${u.nombre} no podrá iniciar sesión. Sus referidos se conservan.`,
      buttons: [
        { text: 'Cancelar', role: 'cancel' },
        { text: 'Desactivar', role: 'destructive', handler: () => { cambiarEstado(u); } },
      ],
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const tituloDialogo = dialogo?.modo === 'crear' ? 'Nuevo usuario'
    : dialogo?.modo === 'editar' ? 'Editar usuario'
    : 'Restablecer contraseña';

  return (
    <IonPage className="app-page">
      <AppBackground />

      <PageHeader
        title="Usuarios"
        subtitle={loading ? 'Cargando…' : `${usuarios.length} usuarios del sistema`}
        backHref="/panel"
        actions={
          <button className="header-action-btn" onClick={() => abrir({ modo: 'crear' })} title="Nuevo usuario">
            <IonIcon icon={addOutline} />
            <span>Nuevo</span>
          </button>
        }
      />

      <IonContent className="content-transparent ion-padding-horizontal">
        <div className="app-container">
          {errorLista && (
            <div className="status-alert alert-error">
              <IonIcon icon={alertCircleOutline} />
              <span>{errorLista}</span>
            </div>
          )}

          {loading ? (
            [1, 2, 3].map(i => (
              <div key={i} className="user-card">
                <IonSkeletonText animated style={{ width: '60%', height: '18px', borderRadius: '8px' }} />
                <IonSkeletonText animated style={{ width: '40%', height: '14px', borderRadius: '8px', marginTop: '8px' }} />
              </div>
            ))
          ) : (
            usuarios.map(u => {
              const esYo = u.id === Number(user?.id);
              return (
                <div key={u.id} className={`user-card ${u.activo ? '' : 'inactive'}`}>
                  <div className="user-card-top">
                    <div className="user-avatar">
                      <IonIcon icon={u.rol === 'admin' ? shieldCheckmarkOutline : personOutline} />
                    </div>
                    <div className="user-info">
                      <h4>{u.nombre}{esYo && <span className="user-you">Tú</span>}</h4>
                      <p>@{u.nickname} · {u.total_referidos} {u.total_referidos === 1 ? 'referido' : 'referidos'}</p>
                    </div>
                  </div>

                  <div className="user-card-bottom">
                    <div className="user-tags">
                      <span className={`user-tag ${u.rol === 'admin' ? 'admin' : ''}`}>
                        {u.rol === 'admin' ? 'Administrador' : 'Asistente'}
                      </span>
                      {!u.activo && <span className="user-tag off">Desactivado</span>}
                    </div>
                    <div className="user-actions">
                      <button className="icon-btn" onClick={() => abrir({ modo: 'editar', usuario: u })} title="Editar">
                        <IonIcon icon={pencilOutline} />
                      </button>
                      <button className="icon-btn" onClick={() => abrir({ modo: 'password', usuario: u })} title="Restablecer contraseña">
                        <IonIcon icon={keyOutline} />
                      </button>
                      <button
                        className={`icon-btn ${u.activo ? 'danger' : 'success'}`}
                        onClick={() => confirmarEstado(u)}
                        disabled={esYo}
                        title={esYo ? 'No puedes desactivarte a ti mismo' : (u.activo ? 'Desactivar' : 'Activar')}
                      >
                        <IonIcon icon={powerOutline} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Diálogo: crear, editar o restablecer contraseña */}
        <IonModal isOpen={dialogo !== null} onDidDismiss={() => setDialogo(null)} className="app-dialog">
          {dialogo && (
            <form className="app-dialog-body app-form" onSubmit={guardar}>
              <div className="app-dialog-header">
                <h3>{tituloDialogo}</h3>
                <button type="button" className="icon-btn" onClick={() => setDialogo(null)} title="Cerrar">
                  <IonIcon icon={closeOutline} />
                </button>
              </div>

              {dialogo.modo === 'password' ? (
                <p className="app-dialog-hint">
                  Asigna una contraseña nueva a <strong>{dialogo.usuario.nombre}</strong> (@{dialogo.usuario.nickname}).
                </p>
              ) : (
                <>
                  <div className="input-group">
                    <span className="input-icon"><IonIcon icon={personOutline} /></span>
                    <IonInput
                      className="app-input"
                      placeholder="Nombre completo"
                      value={nombre}
                      onIonInput={(e: any) => setNombre(e.detail.value ?? '')}
                      disabled={guardando}
                    />
                  </div>

                  <div className="input-group">
                    <span className="input-icon"><IonIcon icon={atOutline} /></span>
                    <IonInput
                      className="app-input"
                      placeholder="Nombre de usuario"
                      value={nickname}
                      onIonInput={(e: any) => setNickname(e.detail.value ?? '')}
                      disabled={guardando}
                    />
                  </div>

                  <div>
                    <label className="option-label">Rol</label>
                    <div className="option-cards">
                      {ROLES.map(r => (
                        <button
                          type="button"
                          key={r.valor}
                          className={`option-card ${rol === r.valor ? 'selected' : ''}`}
                          onClick={() => setRol(r.valor)}
                          disabled={guardando}
                        >
                          {r.etiqueta}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {dialogo.modo !== 'editar' && (
                <div className="input-group">
                  <span className="input-icon"><IonIcon icon={lockClosedOutline} /></span>
                  <IonInput
                    className="app-input"
                    type={verPassword ? 'text' : 'password'}
                    placeholder={`Contraseña (mínimo ${PASSWORD_MIN})`}
                    value={password}
                    onIonInput={(e: any) => setPassword(e.detail.value ?? '')}
                    disabled={guardando}
                  />
                  <span className="input-icon-right" onClick={() => setVerPassword(v => !v)}>
                    <IonIcon icon={verPassword ? eyeOffOutline : eyeOutline} />
                  </span>
                </div>
              )}

              {errorForm && (
                <div className="status-alert alert-error">
                  <IonIcon icon={alertCircleOutline} />
                  <span>{errorForm}</span>
                </div>
              )}

              <IonButton type="submit" expand="block" className="app-btn-primary" disabled={guardando}>
                {guardando ? <IonSpinner name="crescent" /> : 'Guardar'}
              </IonButton>
            </form>
          )}
        </IonModal>

        <IonToast
          isOpen={toast !== ''}
          message={toast}
          duration={2500}
          onDidDismiss={() => setToast('')}
          className="custom-toast"
        />
      </IonContent>
    </IonPage>
  );
};

export default Usuarios;
